import { StatusCardType } from '@/common/helios/OrderStatusCard';
import {
  getCtaLink,
  getCustomAttributesDescription
} from '@/common/helios/OrderStatusCard/utils';
import { Consumer } from '~/constants/common/consumer';
import OrderState from '~/constants/common/OrderState';
import UserPurchaseData from '~/services/common/contracts/UserPurchaseData';

export type OrderStatusCardConfig = {
  bodyText: string | null;
  ctaDescription: string;
  ctaLink: string;
  ctaText: string;
  headlineText: string;
  openVerificationsCount: number | null;
  wrapHeadline: boolean;
};

type GetOrderStatusCardConfigArgs = {
  consumer: Consumer;
  givenName: string;
  make: string;
  openVerifications: UserPurchaseData['openVerifications'];
  orderState: OrderState;
  vehicleId: number;
};

function toStatusCardType(orderState: OrderState): StatusCardType {
  switch (orderState) {
    case OrderState.InProgress:
    case OrderState.InProgressWithTradeIn:
      return StatusCardType.PurchaseInProgress;
    case OrderState.OrderPlaced:
      return StatusCardType.OrderPlaced;
    case OrderState.RecentlyPurchased:
      return StatusCardType.Purchased;
    case OrderState.Reserved:
      return StatusCardType.Reserved;
    default:
      return StatusCardType.PurchaseInProgress;
  }
}

function getBodyText(
  orderState: OrderState,
  hasOpenVerifications: boolean
): string | null {
  switch (orderState) {
    case OrderState.InProgress:
    case OrderState.InProgressWithTradeIn:
      return null;
    case OrderState.OrderPlaced:
      return hasOpenVerifications
        ? null
        : 'All of your tasks are complete! Sit back, relax and get excited for your new ride.';
    case OrderState.RecentlyPurchased:
      return 'Everything for your car, all in one place. View your registration status, report an issue and more.';
    case OrderState.Reserved:
      return "We'll soon be inspecting and perfecting your new ride.";
    default:
      return null;
  }
}

function getCtaText(orderState: OrderState): string {
  switch (orderState) {
    case OrderState.InProgress:
    case OrderState.InProgressWithTradeIn:
      return 'Continue Purchase';
    case OrderState.OrderPlaced:
      return 'View Purchase Details';
    case OrderState.RecentlyPurchased:
      return 'View My Cars Page';
    case OrderState.Reserved:
      return 'Check Status';
    default:
      return 'Continue Purchase';
  }
}

function getHeadlineText(
  orderState: OrderState,
  make: string,
  givenName: string,
  hasOpenVerifications: boolean
): string {
  const nameClause = givenName ? `, ${givenName}` : '';
  switch (orderState) {
    case OrderState.InProgress:
    case OrderState.InProgressWithTradeIn:
      return `it's almost yours${nameClause}!`;
    case OrderState.OrderPlaced:
      return hasOpenVerifications
        ? `it's almost yours${nameClause}!`
        : `congrats on your new ${make}${nameClause}!`;
    case OrderState.RecentlyPurchased:
      return `congrats on your new ${make}${nameClause}!`;
    case OrderState.Reserved:
      return `this ${make} is reserved for you${nameClause}!`;
    default:
      return `it's almost yours${nameClause}!`;
  }
}

function getOpenVerificationsCount(
  orderState: OrderState,
  hasOpenVerifications: boolean,
  count: number
): number | null {
  switch (orderState) {
    case OrderState.InProgress:
    case OrderState.InProgressWithTradeIn:
    case OrderState.OrderPlaced:
      return hasOpenVerifications ? count : null;
    default:
      return null;
  }
}

export function getOrderStatusCardConfig({
  consumer,
  givenName,
  make,
  openVerifications,
  orderState,
  vehicleId
}: GetOrderStatusCardConfigArgs): OrderStatusCardConfig {
  const hasOpenVerifications = openVerifications?.hasOpenVerifications ?? false;
  const verificationCount = openVerifications?.openVerificationsCount ?? 0;
  const cardType = toStatusCardType(orderState);

  return {
    bodyText: getBodyText(orderState, hasOpenVerifications),
    ctaDescription: getCustomAttributesDescription(cardType),
    ctaLink: getCtaLink(cardType, vehicleId, consumer),
    ctaText: getCtaText(orderState),
    headlineText: getHeadlineText(
      orderState,
      make,
      givenName,
      hasOpenVerifications
    ),
    openVerificationsCount: getOpenVerificationsCount(
      orderState,
      hasOpenVerifications,
      verificationCount
    ),
    wrapHeadline:
      orderState !== OrderState.OrderPlaced &&
      orderState !== OrderState.RecentlyPurchased
  };
}
